<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import BaseButton from '@uiKit/elements/Button.vue'
import BaseInputOtp from '@uiKit/elements/InputOtp.vue'
import FormField from '@uiKit/components/FormField.vue'
import AuthTrustChips from './AuthTrustChips.vue'
import { ONE_SECOND_MS } from '@shared/utils'

interface Props {
  email?: string
  submitLabel?: string
  loading?: boolean
  showNavigation?: boolean
  canBack?: boolean
  apiError?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  email: '',
  submitLabel: 'Verify and continue',
  loading: false,
  showNavigation: false,
  canBack: true,
})

const emit = defineEmits<{
  (e: 'verify', code: string): void
  (e: 'resend'): void
  (e: 'back'): void
}>()

const RESEND_COOLDOWN_SECONDS = 20
const OTP_LENGTH = 6

const otp = ref('')
const cooldown = ref(RESEND_COOLDOWN_SECONDS)
const clientError = ref<string | null>(null)
const hasEditedSinceApiError = ref(false)
let cooldownTimer: ReturnType<typeof setInterval> | null = null

const normalizedOtp = computed(() => otp.value.replace(/\D/g, '').slice(0, OTP_LENGTH))
const isOtpValid = computed(() => normalizedOtp.value.length === OTP_LENGTH)

const visibleApiError = computed(() =>
  props.apiError && !hasEditedSinceApiError.value ? props.apiError : null,
)
const visibleError = computed(() => clientError.value ?? visibleApiError.value ?? null)

const startCooldown = () => {
  cooldown.value = RESEND_COOLDOWN_SECONDS
  if (cooldownTimer) clearInterval(cooldownTimer)
  cooldownTimer = setInterval(() => {
    cooldown.value--
    if (cooldown.value <= 0 && cooldownTimer) {
      clearInterval(cooldownTimer)
      cooldownTimer = null
    }
  }, ONE_SECOND_MS)
}

const handleVerify = () => {
  if (props.loading) return
  if (!isOtpValid.value) {
    clientError.value = `Please enter all ${OTP_LENGTH} digits of the code.`
    return
  }
  clientError.value = null
  emit('verify', normalizedOtp.value)
}

const handleResend = () => {
  if (cooldown.value > 0) return
  otp.value = ''
  clientError.value = null
  emit('resend')
  startCooldown()
}

onMounted(startCooldown)

onUnmounted(() => {
  if (cooldownTimer) clearInterval(cooldownTimer)
})

watch(
  otp,
  (value) => {
    const safeValue = String(value ?? '')
    if (safeValue !== normalizedOtp.value) otp.value = normalizedOtp.value
    if (clientError.value && isOtpValid.value) clientError.value = null
    hasEditedSinceApiError.value = true
  },
  { immediate: true },
)

watch(
  () => props.apiError,
  () => {
    hasEditedSinceApiError.value = false
  },
)
</script>

<template>
  <Box class="otp-form" direction="col" gap="6">
    <BaseButton
      v-if="props.showNavigation && props.canBack"
      variant="text"
      color="secondary"
      size="small"
      icon="arrow-left"
      label="Back"
      class="otp-form__back"
      @click="emit('back')"
    />

    <Box direction="col" gap="2">
      <Text is="h1" type="display" weight="bold">Check your email</Text>
      <Text type="body" color="muted">
        We sent a 6-digit code to
        <Text is="span" weight="semibold" color="default">
          {{ props.email || 'your email address' }}
        </Text>
      </Text>
    </Box>

    <Box direction="col" gap="4">
      <FormField :error="visibleError ?? undefined">
        <BaseInputOtp v-model="otp" :length="OTP_LENGTH" class="otp-form__input" />
      </FormField>

      <BaseButton
        block
        size="large"
        :label="props.submitLabel"
        :loading="props.loading"
        :disabled="props.loading"
        @click="handleVerify"
      />

      <Box align="center" justify="center" gap="1" class="otp-form__resend">
        <Text type="caption" color="muted">Didn't receive it?</Text>
        <BaseButton
          variant="link"
          size="small"
          :color="cooldown > 0 ? 'secondary' : 'primary'"
          :disabled="cooldown > 0"
          :label="cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'"
          @click="handleResend"
        />
      </Box>
    </Box>

    <AuthTrustChips />
  </Box>
</template>

<style scoped>
.otp-form {
  width: 100%;
}

.otp-form__back {
  width: fit-content;
}

.otp-form__input :deep(.p-inputotp) {
  display: flex;
  gap: 0.625rem;
  width: 100%;
}

.otp-form__input :deep(.p-inputotp-input) {
  flex: 1;
  min-width: 0;
  width: auto;
  height: 56px;
  padding: 0;
  border-radius: 10px;
  border: 1px solid var(--p-content-border-color);
  font-size: 1.25rem;
  font-weight: 600;
  text-align: center;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.otp-form__input :deep(.p-inputotp-input:focus),
.otp-form__input :deep(.p-inputotp-input:focus-visible) {
  border-color: var(--p-primary-500);
  box-shadow: 0 0 0 4px rgba(103, 85, 251, 0.12);
  outline: none;
}

@media (max-width: 992px) {
  .otp-form__input :deep(.p-inputotp) {
    gap: 0.5rem;
  }

  .otp-form__input :deep(.p-inputotp-input) {
    height: 52px;
    font-size: 1.125rem;
  }
}
</style>
